// whatsapp-web-signals.ts — which signal decides "is WhatsApp reaching this space".
//
// The space-level alarm ("you are not receiving WhatsApp messages") comes from the Meta webhook
// check. That check says nothing about a QR account: such an account has no Meta webhook, so a
// space whose only accounts are QR would show a credential failure for a channel that does not
// have one. For QR accounts the signal is the session state and the bridge lease (the same status
// the account row shows). This module scopes the webhook result to the accounts it can speak for.

import type { WebhookHealthStatus } from '@/hooks/useWebhookErrorDetection';

export interface WebAccountsScope {
  /** The space has at least one active account that is not a QR account. */
  hasMetaAccounts: boolean;
  /** An active QR account needs the bridge and its lease ran out (or was never taken). */
  bridgeDown: boolean;
}

/** What a space with nothing to measure through Meta reads: healthy, no calls, no errors. */
const NOTHING_TO_MEASURE: WebhookHealthStatus = {
  isHealthy: true,
  recentErrors: [],
  successRate: 0,
  totalCalls: 0,
  authenticationFailing: false,
};

export function scopeWebhookStatusToMeta(status: WebhookHealthStatus, scope: WebAccountsScope): WebhookHealthStatus {
  const base = scope.hasMetaAccounts ? status : NOTHING_TO_MEASURE;
  // isHealthy false is what makes every existing screen show the alarm; `bridgeDown` makes the
  // alarm say what is wrong instead of reporting a Meta credential.
  return scope.bridgeDown ? { ...base, isHealthy: false, bridgeDown: true } : base;
}
